import {
  Button,
  Dropdown,
  Modal,
  ResponsiveContainer,
} from '@prio365/prio365-react-library';
import { notification } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import PrioSpinner from '../../../components/PrioSpinner';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import classnames from 'classnames';
import { useTheme } from 'react-jss';
import {
  getActiveProjectProject,
  getCurrentDriveItem,
  getCurrentFolderChildren,
  getCurrentFolderItem,
  getDocumentsMetaAreMultipleSelected,
  getUserMe,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import UploadField, { PrioFile } from '../../../components/Upload/UploadField';
import { NewDocumentType } from '../../../models/Document';
import { DriveItem } from '../../../models/Drive';
import { Project } from '../../../models/Project';
import { GroupId, ProjectId, ProjectRole } from '../../../models/Types';
import { User } from '../../../models/User';
import { PrioTheme } from '../../../theme/types';
import { makePrioStyles } from '../../../theme/utils';
import { CreateFolderRequest } from '../../settings/components/NewFolderForm';
import NewFolderModal from '../../settings/components/NewFolderModal';
import {
  driveItemNewFolderCreated,
  DriveItemsFetchContext,
  fetchDriveItemsSagaAction,
} from '../actions';
import { setDocumentsMetaState } from '../actions/meta';
import { setCurrentPreviewModalVisibility } from '../actions/previewModal';
import {
  addDriveItemPermissions,
  apiCreateDriveFolder,
  apiCreateRemoteItemsFromRoot,
  apiDownloadDriveItems,
  apiMergePdfs,
  apiZipArchive,
  apiZipDownload,
} from '../api';
import { fetchRemoteFolders } from '../actions/remoteFolders';
import { checkUploadConflicts, isDriveItemFolder } from '../util';
import CreateNewDriveItemDropdownOptions from './CreateNewDriveItemDropdownOptions';
import { NewDocumentDrawer } from './Drawers/NewDocumentDrawer';
import { NewDocumentFromTemplateDrawer } from './Drawers/NewDocumentFromTemplateDrawer';
import { LegacyNewDocumentFromTemplateDrawer } from './Drawers/LegacyNewDocumentFromTemplateDrawer';
import { sagaStartUploadFiles } from '../sagas/watchUploadFiles';
import { addPrioFilesToUploadList } from '../actions/uploadLists';
import { sagaUploadFiles } from '../sagas/watchUploadFiles';
import { FileNameConflictBehaviour } from './Modals/DriveItemConflictModal';
import MergePdfDrawer from './MergePdfDrawer';
import ZipFolderModal from './Modals/ZipFolderModal';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: 64,
    minHeight: 64,
    borderBottom: theme.old.borders.sub,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    padding: `0px 24px`,
  },
  row: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  contextPartContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.regular,
    padding: `0px ${theme.spacing.regular}px`,
    height: 64,
    minHeight: 64,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  contextPart: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  selectedItemCount: {
    fontStyle: 'italic',
    color: 'rgba(0, 0, 0, 0.45)',
    userSelect: 'none',
    margin: 0,
    whiteSpace: 'nowrap',
    width: '105px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  dropDownButton: {
    height: '32px',
    justifyContent: 'space-evenly',
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    border: '1px solid transparent',
    '&:hover': {
      backgroundColor: theme.colors.base.blue[50],
    },
  },
  dropdown: {
    padding: 0,
    zIndex: 999,
  },
  dropDownOption: {
    paddingLeft: theme.old.spacing.unit(1),
    height: '32px',
    display: 'flex',
    alignItems: 'center',
  },
  disabled: {
    cursor: 'not-allowed',
    color: theme.colors.application.typography.muted,
  },
}));

interface DocumentsNavigationBarProps {
  projectId: ProjectId;
  groupId: GroupId;
  selectedDriveItems?: DriveItem[];
  setSelectedDriveItems?: (selectedDriveItems: DriveItem[]) => void;
}

export const DocumentsNavigationBar: React.FC<DocumentsNavigationBarProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const { projectId, groupId, selectedDriveItems, setSelectedDriveItems } =
    props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const pathPrefix = './';
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const driveItem = useSelector<RootReducerState, DriveItem>(
    getCurrentDriveItem
  );

  const [downloadModalVisible, setDownloadModalVisible] =
    useState<boolean>(false);

  const [zipModalVisible, setZipModalVisible] = useState<boolean>(false);
  const isRoot = driveItem?.name === 'root';

  const [openNewDocFromTemplateDrawer, setOpenNewDocFromTemplateDrawer] =
    useState<boolean>(false);
  const [openLegacyDocFromTemplateDrawer, setOpenLegacyDocFromTemplateDrawer] =
    useState<boolean>(false);
  const [openNewDocDrawer, setOpenNewDocDrawer] = useState<boolean>(false);
  const [documentType, setDocumentType] = useState<NewDocumentType>(null);
  const [prefix, setprefix] = useState<string>(null);

  const selectedDriveItemsWithoutFolder = useMemo(
    () => selectedDriveItems?.filter((item) => !isDriveItemFolder(item)) ?? [],
    [selectedDriveItems]
  );

  const foldersInSelection = useMemo(
    () => (selectedDriveItems ?? []).some((item) => isDriveItemFolder(item)),
    [selectedDriveItems]
  );

  const rootFolder = useSelector<RootReducerState, DriveItem>((state) =>
    getCurrentFolderItem(state, `root-group-${groupId}`)
  );
  const userMe = useSelector<RootReducerState, User>(getUserMe);
  const activeProject = useSelector<RootReducerState, Project>(
    getActiveProjectProject
  );
  const activeOneDriveSyncEnabled =
    !!activeProject && !!userMe?.mail && !!rootFolder?.sharepointIds?.siteId;

  const areMultipleSelected = useSelector(getDocumentsMetaAreMultipleSelected);

  const currentFolder = useSelector<RootReducerState, DriveItem>((state) =>
    getCurrentFolderItem(state, driveItem?.id ?? `root-group-${groupId}`)
  );

  const [newFolderModalOpen, setNewFolderModalOpen] = useState<boolean>(false);
  const [newFolderCreating, setNewFolderCreating] = useState<boolean>(false);
  const [isDropdownNewDocumentOpen, setIsDropdownNewDocumentOpen] =
    useState(false);

  const myProjectRoles: ProjectRole[] = useMemo(() => {
    return userMe?.prioData?.projectRoles[projectId] ?? [];
  }, [userMe, projectId]);

  const isRootAndProjectAdmin =
    isRoot && myProjectRoles?.includes('projectAdmin');

  const currentFolderDriveItemId = driveItem?.id ?? `root-group-${groupId}`;
  const rootDriveItemFolderId = rootFolder?.id ?? `root-group-${groupId}`;

  const currentFolderChildren = useSelector<RootReducerState, DriveItem[]>(
    (state) => getCurrentFolderChildren(state, currentFolderDriveItemId) ?? []
  );

  const [uploadFieldKey, setUploadFieldKey] = useState(0);
  const [mergeDrawerOpen, setMergeDrawerOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [syncingProject, setSyncingProject] = useState(false);

  const pdfItems =
    selectedDriveItems?.filter(
      (item) => item.file?.mimeType === 'application/pdf'
    ) || [];
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleBeforeUpload = (originFiles: PrioFile[], sessionId: string) => {
    if (!currentFolderChildren || currentFolderChildren.length === 0) {
      dispatch(
        sagaStartUploadFiles(
          groupId,
          sessionId,
          currentFolderDriveItemId ?? rootDriveItemFolderId,
          projectId
        )
      );
      setTimeout(() => setUploadFieldKey((k) => k + 1), 100);
      return true;
    }
    const { hasFolderConflict, hasFileConflict } = checkUploadConflicts(
      originFiles,
      currentFolderChildren
    );
    if (hasFolderConflict || hasFileConflict) {
      dispatch(
        setDocumentsMetaState({
          driveItemConflictModalState: {
            behavior: 'upload',
            context: DriveItemsFetchContext.DocumentsPage,
            modelType: 'file',
            destinationProjectId: projectId,
            selectedDriveItems: [],
            destinationFolderId: currentFolderDriveItemId,
            sessionId,
            fileList: originFiles,
          },
          showDriveItemConflictModalState: 'document',
        })
      );
      setTimeout(() => setUploadFieldKey((k) => k + 1), 100);
      return false;
    }
    dispatch(
      sagaStartUploadFiles(
        groupId,
        sessionId,
        currentFolderDriveItemId ?? rootDriveItemFolderId,
        projectId
      )
    );
    setTimeout(() => setUploadFieldKey((k) => k + 1), 100);
    return true;
  };

  const handleAfterUpload = (fileList: PrioFile[], sessionId: string) => {
    dispatch(
      addPrioFilesToUploadList(
        groupId,
        fileList.map(
          ({ fileId, name, webkitRelativePath, size, type, sessionId }) => ({
            fileId,
            sessionId,
            parentDriveItemId:
              currentFolderDriveItemId ?? rootDriveItemFolderId,
            name,
            path: webkitRelativePath,
            size,
            isFolder: false,
            mimeType: type,
            driveItemId: null,
          })
        )
      )
    );
    dispatch(
      sagaUploadFiles(
        groupId,
        sessionId,
        currentFolderDriveItemId ?? rootDriveItemFolderId,
        projectId,
        fileList,
        FileNameConflictBehaviour.RENAME
      )
    );
    setTimeout(() => setUploadFieldKey((k) => k + 1), 100);
  };

  const onCreateDocumentSuccess = (
    folder?: DriveItem,
    selectedProjectId?: ProjectId
  ) => {
    setOpenNewDocFromTemplateDrawer(false);
    setOpenLegacyDocFromTemplateDrawer(false);
    setOpenNewDocDrawer(false);
    if (folder?.id) {
      if (selectedProjectId === projectId) {
        const isFolderRoot = folder?.name === 'root';
        navigate(
          !isFolderRoot
            ? `${pathPrefix}folder/${folder.id}`
            : `${pathPrefix}all`
        );
      }
    } else {
      navigate(
        driveItem && !isRoot
          ? `${pathPrefix}folder/${driveItem?.id}`
          : `${pathPrefix}all`
      );
    }
  };

  const createFolder = useCallback(async () => {
    if (!!groupId || isRootAndProjectAdmin) {
      setNewFolderModalOpen(true);
    }
  }, [groupId, isRootAndProjectAdmin]);

  const showMoveDrawer = useCallback(() => {
    dispatch(
      setDocumentsMetaState({
        driveItemConflictModalState: {
          behavior: 'move',
          context: DriveItemsFetchContext.DocumentsPage,
          modelType: 'driveItem',
          selectedDriveItems: selectedDriveItems,
          sourceFolderId: driveItem?.id,
          sourceProjectId: projectId,
          destinationFolderId: driveItem?.id,
          destinationProjectId: projectId,
        },
        showDriveItemConflictModalState: 'document',
      })
    );
    setSelectedDriveItems([]);
  }, [
    dispatch,
    setSelectedDriveItems,
    driveItem?.id,
    projectId,
    selectedDriveItems,
  ]);

  const showCopyDrawer = useCallback(() => {
    dispatch(
      setDocumentsMetaState({
        driveItemConflictModalState: {
          behavior: 'copy',
          context: DriveItemsFetchContext.DocumentsPage,
          modelType: 'driveItem',
          selectedDriveItems: selectedDriveItems,
          sourceFolderId: driveItem?.id,
          sourceProjectId: projectId,
          destinationFolderId: driveItem?.id,
          destinationProjectId: projectId,
        },
        showDriveItemConflictModalState: 'document',
      })
    );
    setSelectedDriveItems([]);
  }, [
    dispatch,
    setSelectedDriveItems,
    driveItem?.id,
    projectId,
    selectedDriveItems,
  ]);

  const openPreviewToEditMetaData = useCallback(() => {
    dispatch(
      setCurrentPreviewModalVisibility(
        true,
        selectedDriveItems[0]?.id,
        groupId,
        selectedDriveItems[0],
        selectedDriveItemsWithoutFolder,
        null,
        projectId,
        '',
        false,
        selectedDriveItemsWithoutFolder,
        'documents'
      )
    );
    setSelectedDriveItems([]);
  }, [
    dispatch,
    selectedDriveItems,
    selectedDriveItemsWithoutFolder,
    groupId,
    projectId,
    setSelectedDriveItems,
  ]);

  const showDeleteModal = useCallback(() => {
    dispatch(
      setDocumentsMetaState({
        showDeleteDocumentsModal: true,
      })
    );
  }, [dispatch]);

  const downloadNonFolderItems = useCallback(async () => {
    await apiDownloadDriveItems(groupId, selectedDriveItemsWithoutFolder);
  }, [groupId, selectedDriveItemsWithoutFolder]);

  const downloadAllItems = useCallback(async () => {
    const zipDownloadKey = 'zip-download-preparing';
    notification.open({
      key: zipDownloadKey,
      message: t('documents:errorMessages.downloadZipPreparing'),
      icon: <PrioSpinner size="small" />,
      duration: 0,
    });
    const { result } = await apiZipDownload(
      groupId,
      selectedDriveItems.map((item) => item.id)
    );
    notification.close(zipDownloadKey);
    if (!result.ok) {
      notification.open({
        message: t('common:error'),
        description: t('documents:errorMessages.downloadFileError', {
          name:
            selectedDriveItems.length === 1 ? selectedDriveItems[0].name : '',
          count: selectedDriveItems.length,
        }),
      });
    }
  }, [groupId, selectedDriveItems, t]);

  const downloadFiles = useCallback(() => {
    if (foldersInSelection) {
      downloadAllItems();
    } else {
      downloadNonFolderItems();
    }
    setDownloadModalVisible(false);
    setSelectedDriveItems([]);
  }, [
    foldersInSelection,
    downloadAllItems,
    downloadNonFolderItems,
    setSelectedDriveItems,
  ]);

  const zipFiles = useCallback(
    async (zipName?: string) => {
      const { result } = await apiZipArchive(
        groupId,
        currentFolderDriveItemId,
        selectedDriveItems.map((item) => item.id),
        zipName
      );
      if (!result.ok) {
        const onlyFoldersInSelection = selectedDriveItems.every((item) =>
          isDriveItemFolder(item)
        );
        notification.open({
          message: t('common:error'),
          description: t(
            `documents:errorMessages.zip${onlyFoldersInSelection ? 'Folder' : 'File'}Error`,
            {
              name:
                selectedDriveItems.length === 1
                  ? selectedDriveItems[0].name
                  : '',
              count: selectedDriveItems.length,
            }
          ),
        });
      }
      setZipModalVisible(false);
      setSelectedDriveItems([]);
    },
    [
      groupId,
      currentFolderDriveItemId,
      selectedDriveItems,
      setSelectedDriveItems,
      t,
    ]
  );

  const onNewFolderOk = async (value: CreateFolderRequest) => {
    if (value.folderName === '') {
      closeNewFolderModal();
      return;
    }
    setNewFolderCreating(true);
    const { data } = await apiCreateDriveFolder(
      groupId,
      driveItem?.id,
      value.folderName
    );
    if (isRoot)
      await addDriveItemPermissions(data.id, value, projectId, groupId);
    setNewFolderCreating(false);
    if (data) {
      dispatch(driveItemNewFolderCreated(data, driveItem?.id ?? null, groupId));
    } else {
      notification.open({
        message: t('common:error'),
        description: t('documents:errorMessages.newFolderCreateError'),
      });
    }
    closeNewFolderModal();
  };

  const onNewFolderCancel = () => {
    closeNewFolderModal();
  };

  const closeNewFolderModal = () => {
    setNewFolderModalOpen(false);
  };

  const onRemoveItem = (id: string) => {
    if (setSelectedDriveItems && selectedDriveItems) {
      setSelectedDriveItems(
        selectedDriveItems.filter((item) => item.id !== id)
      );
    }
  };

  const hasNonPdf = (selectedDriveItems: DriveItem[]) =>
    selectedDriveItems.some(
      (item) => item.file?.mimeType !== 'application/pdf'
    );

  const handleOpenMergeDrawer = () => setMergeDrawerOpen(true);
  const handleCloseMergeDrawer = () => setMergeDrawerOpen(false);

  const handleMergePdfs = async (name: string, driveItemIds: string[]) => {
    setMerging(true);
    try {
      const { result } = await apiMergePdfs(groupId, { name, driveItemIds });
      if (result.status >= 200 && result.status < 300) {
        notification.success({
          message: t('documents:mergePdf.success'),
        });
        setMergeDrawerOpen(false);
        setSelectedDriveItems?.([]);
        dispatch(
          fetchDriveItemsSagaAction(
            projectId,
            groupId,
            currentFolderDriveItemId ?? rootDriveItemFolderId,
            isRoot,
            DriveItemsFetchContext.DocumentsPage
          )
        );
      } else {
        notification.error({
          message: t('common:error'),
          description: t('documents:mergePdf.error'),
        });
      }
    } finally {
      setMerging(false);
    }
  };

  const handleSyncProject = async () => {
    setSyncingProject(true);
    notification.info({
      message: t('documents:syncProject.inProgress.message'),
      description: t('documents:syncProject.inProgress.description'),
    });
    try {
      const { result } = await apiCreateRemoteItemsFromRoot(projectId, groupId);
      if (result.status >= 200 && result.status < 300) {
        notification.success({
          message: t('documents:syncProject.success'),
        });
        dispatch(fetchRemoteFolders());
      } else {
        notification.error({
          message: t('common:error'),
          description: t('documents:syncProject.error'),
        });
      }
    } finally {
      setSyncingProject(false);
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion
  return (
    <>
      <div id="prio-module-navigation-bar" className={classes.root}>
        <div className={classes.row}>
          <ResponsiveContainer
            children={[
              <Dropdown
                overlayClassName={classes.dropdown}
                overlay={
                  <CreateNewDriveItemDropdownOptions
                    projectId={projectId}
                    setOpenNewDocFromTamplateDrawer={
                      setOpenNewDocFromTemplateDrawer
                    }
                    setOpenLegacyDocFromTemplateDrawer={
                      setOpenLegacyDocFromTemplateDrawer
                    }
                    setOpenNewDocDrawer={setOpenNewDocDrawer}
                    setIsDropdownNewDocumentOpen={setIsDropdownNewDocumentOpen}
                    onDocumentTypeSelect={(id, prefix) => {
                      setDocumentType(id);
                      setprefix(prefix);
                    }}
                    isRoot={isRoot}
                    onNewFolderClick={createFolder}
                    disableFolderMenuItem={
                      (currentFolder?.name === 'root' ||
                        rootFolder?.id === driveItem?.id) &&
                      !isRootAndProjectAdmin
                    }
                  />
                }
                onVisibleChange={(visible) =>
                  setIsDropdownNewDocumentOpen(visible)
                }
                visible={isDropdownNewDocumentOpen}
                trigger={['click']}
              >
                <Button
                  type="primary"
                  iconProp={['fal', 'plus']}
                  disabled={
                    (currentFolder?.name === 'root' ||
                      rootFolder?.id === driveItem?.id) &&
                    !isRootAndProjectAdmin
                  }
                >
                  <span>{t('documents:navigationBar.createNew')}</span>
                </Button>
              </Dropdown>,
              <Button
                disabled={!areMultipleSelected}
                iconProp={['fal', 'copy']}
                type="default"
                onClick={showCopyDrawer}
              >
                {t('documents:navigationBar.copy')}
              </Button>,
              <Button
                disabled={!areMultipleSelected}
                iconProp={['fal', 'file-export']}
                onClick={showMoveDrawer}
                type="default"
              >
                {t('documents:navigationBar.move')}
              </Button>,
              <Button
                type="default"
                disabled={
                  !areMultipleSelected || !!location.pathname.match(/(all)$/)
                }
                iconProp={['fal', 'trash']}
                onClick={showDeleteModal}
              >
                {t('documents:navigationBar.delete')}
              </Button>,

              <Button
                type="default"
                iconProp={['fal', 'arrow-to-bottom']}
                onClick={() => {
                  if (selectedDriveItems.length < 10) {
                    downloadFiles();
                  } else {
                    setDownloadModalVisible(true);
                  }
                }}
                disabled={selectedDriveItems.length === 0}
              >
                {t('documents:navigationBar.download')}
              </Button>,
              <Button
                type="default"
                iconProp={['fal', 'file-zipper']}
                onClick={() => setZipModalVisible(true)}
                disabled={selectedDriveItems.length === 0}
              >
                {t('documents:navigationBar.zip')}
              </Button>,
              <Dropdown
                overlay={
                  <div
                    style={{
                      fontSize: theme.font.fontSize.small,
                    }}
                  >
                    <div
                      className={classnames(
                        'prio-dropdown-option-root',
                        classes.dropDownOption,
                        {
                          [classes.disabled]:
                            currentFolder?.name === 'root' ||
                            rootFolder?.id === driveItem?.id,
                        }
                      )}
                    >
                      <UploadField
                        key={uploadFieldKey + '-file'}
                        isButton
                        multiple
                        onBeforeUpload={handleBeforeUpload}
                        onAfterUpload={handleAfterUpload}
                        disabled={
                          currentFolder?.name === 'root' ||
                          rootFolder?.id === driveItem?.id
                        }
                        className={classnames({
                          [classes.disabled]:
                            currentFolder?.name === 'root' ||
                            rootFolder?.id === driveItem?.id,
                        })}
                      >
                        {t('documents:navigationBar.uploadFile')}
                      </UploadField>
                    </div>
                    <div
                      className={classnames(
                        'prio-dropdown-option-root',
                        classes.dropDownOption,
                        {
                          [classes.disabled]:
                            currentFolder?.name === 'root' ||
                            (rootFolder?.id === driveItem?.id &&
                              !isRootAndProjectAdmin),
                        }
                      )}
                    >
                      <UploadField
                        key={uploadFieldKey + '-folder'}
                        isButton
                        multiple
                        directory
                        onBeforeUpload={handleBeforeUpload}
                        onAfterUpload={handleAfterUpload}
                        disabled={
                          (currentFolder?.name === 'root' ||
                            rootFolder?.id === driveItem?.id) &&
                          !isRootAndProjectAdmin
                        }
                        className={classnames({
                          [classes.disabled]:
                            currentFolder?.name === 'root' ||
                            (rootFolder?.id === driveItem?.id &&
                              !isRootAndProjectAdmin),
                        })}
                      >
                        {t('documents:navigationBar.uploadFolder')}
                      </UploadField>
                    </div>
                  </div>
                }
              >
                <Button
                  suffixIconProp={['fal', 'chevron-down']}
                  type="default"
                  iconProp={['fal', 'upload']}
                  disabled={
                    currentFolder?.name === 'root' ||
                    (rootFolder?.id === driveItem?.id && !isRootAndProjectAdmin)
                  }
                >
                  {t('documents:navigationBar.upload')}
                </Button>
              </Dropdown>,
              <Button
                disabled={
                  !(
                    areMultipleSelected &&
                    selectedDriveItemsWithoutFolder.length > 0
                  )
                }
                iconProp={['fal', 'pen']}
                type="default"
                onClick={openPreviewToEditMetaData}
              >
                {t('documents:navigationBar.editMetaData')}
              </Button>,
              <Button
                iconProp={['fal', 'file-pdf']}
                type="default"
                disabled={
                  !selectedDriveItems ||
                  selectedDriveItems.length < 2 ||
                  hasNonPdf(selectedDriveItems)
                }
                onClick={handleOpenMergeDrawer}
              >
                {t('documents:mergePdf.action')}
              </Button>,
              <Button
                iconProp={['fal', 'cloud']}
                type="default"
                disabled={!activeOneDriveSyncEnabled || syncingProject}
                onClick={handleSyncProject}
              >
                <span>{t('documents:syncProject.action')}</span>
              </Button>,
              <Button
                href={driveItem?.webUrl}
                disabled={!activeOneDriveSyncEnabled}
                iconProp={['fal', 'external-link-alt']}
                type="default"
              >
                <span>{t('documents:navigationBar.openInSharePoint')}</span>
              </Button>,
            ]}
          />
        </div>
      </div>
      <NewFolderModal
        currentPath={[
          driveItem?.name === 'root'
            ? t('documents:rootFolder')
            : (driveItem?.name ?? ''),
        ]}
        visible={
          newFolderModalOpen &&
          (driveItem?.name !== 'root' || isRootAndProjectAdmin)
        }
        onOk={onNewFolderOk}
        onCancel={onNewFolderCancel}
        confirmLoading={newFolderCreating}
        usePermissions={isRootAndProjectAdmin}
        groupId={groupId}
        parentReferenceId={driveItem?.id ?? rootDriveItemFolderId}
      />
      <NewDocumentFromTemplateDrawer
        driveItem={driveItem}
        projectId={projectId}
        groupId={groupId}
        onSuccess={onCreateDocumentSuccess}
        onCancel={() => {
          setOpenNewDocFromTemplateDrawer(false);
        }}
        visible={openNewDocFromTemplateDrawer}
      />
      <LegacyNewDocumentFromTemplateDrawer
        driveItem={driveItem}
        projectId={projectId}
        groupId={groupId}
        onSuccess={onCreateDocumentSuccess}
        onCancel={() => {
          setOpenLegacyDocFromTemplateDrawer(false);
        }}
        visible={openLegacyDocFromTemplateDrawer}
      />
      <NewDocumentDrawer
        projectId={projectId}
        groupId={groupId}
        onSuccess={onCreateDocumentSuccess}
        onCancel={() => {
          setOpenNewDocDrawer(false);
        }}
        visible={openNewDocDrawer}
        parentFolderDriveItem={currentFolder}
        driveItem={driveItem}
        documentType={documentType}
        prefix={prefix}
      />
      <Modal
        visible={downloadModalVisible}
        title={t('documents:navigationBar.modals.download.title')}
        onOk={downloadFiles}
        onClose={() => setDownloadModalVisible(false)}
        okText={t('documents:navigationBar.modals.download.okText')}
        cancelText={t('documents:navigationBar.modals.download.cancelText')}
        closable
      >
        <p>
          {t('documents:navigationBar.modals.download.content', {
            count: selectedDriveItemsWithoutFolder.length,
          })}
        </p>
      </Modal>
      <ZipFolderModal
        visible={zipModalVisible}
        onOk={(zipName) => {
          setZipModalVisible(false);
          zipFiles(zipName);
        }}
        onCancel={() => setZipModalVisible(false)}
      />
      <MergePdfDrawer
        visible={mergeDrawerOpen}
        onClose={handleCloseMergeDrawer}
        onMerge={handleMergePdfs}
        groupId={groupId}
        pdfItems={pdfItems}
        onRemoveItem={onRemoveItem}
        merging={merging}
      />
    </>
  );
};

export default DocumentsNavigationBar;
